import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Briefcase, BarChart3, LogOut, ArrowRight } from "lucide-react";

const CARDS = [
  {
    key: "bd",
    title: "BD",
    tag: "Business Development",
    description: "IC pipeline, Property IC tool, scenario P&L, MIS-driven deal evaluation, AI suggest, member estimates.",
    bullets: ["Live IMAP pipeline", "3 room-breakdown options", "Per-deal P&L + ROI", "BD member averaging"],
    icon: Briefcase,
    accent: "#FF69B4",
    bg: "from-pink-50 via-amber-50 to-yellow-50",
    border: "border-pink-200",
    target: "/dashboard",
    testid: "persona-bd",
  },
  {
    key: "mis",
    title: "MIS Analysis",
    tag: "Expense intelligence",
    description: "Network-wide expense roll-up, property-wise expense tracker, outlier flags, opex ratio benchmarking, AI cost forensics.",
    bullets: ["Top cost cities", "Property expense MoM", "Outlier auto-flags", "Compare 2-4 properties"],
    icon: BarChart3,
    accent: "#20B2AA",
    bg: "from-teal-50 via-cyan-50 to-emerald-50",
    border: "border-teal-200",
    target: "/mis/dashboard",
    testid: "persona-mis",
  },
];

export default function PersonaChooser() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="min-h-screen bg-brand-ink text-zinc-100" data-testid="persona-chooser">
      {/* Top strip */}
      <header className="px-8 py-5 flex items-center justify-between border-b border-zinc-800/80">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-brand-yellow flex items-center justify-center font-display font-black text-brand-ink text-sm">G</div>
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">goSTOPS</div>
            <div className="font-display font-bold text-[14px] tracking-tight">BD Command Center</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Signed in as</div>
            <div className="text-[12.5px] font-bold">{user?.name || user?.email || "Member"}</div>
          </div>
          <button
            onClick={logout}
            data-testid="persona-logout"
            className="p-2 rounded-full border border-zinc-700 hover:border-brand-yellow hover:text-brand-yellow text-zinc-400"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Greeting + chooser */}
      <main className="px-8 py-12 max-w-7xl mx-auto">
        <div className="mb-10">
          <div className="font-mono-tech text-[10.5px] uppercase tracking-[0.3em] text-zinc-500">{greeting},</div>
          <h1 className="font-display font-black text-[40px] md:text-[52px] tracking-tight leading-tight mt-1">
            {(user?.name || "there").split(" ")[0]}.
            <span className="text-zinc-500"> Which hat today?</span>
          </h1>
          <p className="text-[13px] text-zinc-400 mt-2 max-w-xl">
            Pick the workspace you want to step into. You can switch any time from the top bar — your data is the same, the lens is different.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => navigate(c.target)}
                data-testid={c.testid}
                className={`group text-left bg-gradient-to-br ${c.bg} ${c.border} border-2 rounded-2xl p-7 transition-all hover:scale-[1.01] hover:shadow-2xl text-zinc-900`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: c.accent, color: "white" }}
                  >
                    <Icon className="w-6 h-6" />
                  </span>
                  <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:translate-x-1 group-hover:text-zinc-900 transition-all" />
                </div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">{c.tag}</div>
                <h2 className="font-display font-black text-[32px] tracking-tight leading-none mt-1.5">{c.title}</h2>
                <p className="text-[13px] text-zinc-700 mt-3 leading-snug">{c.description}</p>
                <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {c.bullets.map((b) => (
                    <li key={b} className="text-[11.5px] text-zinc-800 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full" style={{ background: c.accent }} />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-ink text-white text-[11.5px] font-bold uppercase tracking-wider">
                  Enter {c.title}
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[10.5px] text-zinc-600 mt-10 text-center">
          Tip · use <kbd className="font-mono-tech text-[10px] px-1.5 py-0.5 border border-zinc-700 rounded text-zinc-400">⌘K</kbd> inside any workspace to jump straight to a property or IC.
        </p>
      </main>
    </div>
  );
}
